/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react';
import type { ToggleModalProps } from '../../../../../../components/modal/types';
import type { Tag as TagType } from '@whizz/api-model';
import type { AxiosError } from 'axios';
import { useI18n } from '@whizz/react-i18n';
import { Button, Input } from '../../../../../../components';
import { CancelButtonModal, FormGenericError, SaveButton } from '../../../../../../global';
import { GLOBAL_ICONS, QUERIES_IDS } from '../../../../../../util/vars';
import { ColorPicker } from '../../../../modules/ColorPicker';
import { getDefaultTagColor } from '../../../../../../util/styled';
import { repository } from '@whizz/api-client';
import { useMutation, useQueryClient } from 'react-query';
import { Field, Formik } from 'formik';
import * as Yup from 'yup';
import { ModalFooter } from '../../../../../../components/modal/Modal';

const MIN_LABEL_WIDTH = 150;

const AddEditTagModal = ({ data, toggle, tagCount = 1, onSuccess, onError, onDelete }: ModalProps): JSX.Element => {
  const { translate } = useI18n();
  const queryClient = useQueryClient();

  const editMode = null != data;
  const hasStudents = 0 < Number((data as TagWithStudentCount | null | undefined)?.studentCount ?? 0);
  const showDeleteSection = editMode && null != onDelete;
  const isDefaultTag = !!data?.isDefault;

  const mutations = {
    create: useMutation(
      ({ formData }: { formData: FormData }) => repository.TagsRepository.addTag(formData),
      {
        onSuccess: () => {
          queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.TAGS ]);
          onSuccess?.();
          toggle(false);
        },
        onError: (error: AxiosError<{ error: string }>) => {
          if (null == error.response) {
            return;
          }
          onError?.();
        },
      },
    ),
    update: useMutation(
      ({ id, formData }: { id: number, formData: FormData }) => repository.TagsRepository.updateTag(id, formData),
      {
        onSuccess: () => {
          queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.TAGS ]);
          queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.STUDENTS ]);
          onSuccess?.();
          toggle(false);
        },
        onError: (error: AxiosError<{ error: string }>) => {
          if (null == error.response) {
            return;
          }
          onError?.();
        },
      },
    ),
  };

  const handleDelete = () => {
    if (null == data || null == onDelete) {
      return;
    }

    toggle(false);
    onDelete(data);
  };

  const handleSubmit = (formData: FormData) => {
    if (!editMode) {
      mutations.create.mutate({ formData });
    } else {
      mutations.update.mutate({ id: data!.id, formData });
    }
  };

  const VALIDATION_SCHEMA = Yup.object().shape({
    abbreviation: Yup.string()
      .required(translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.VALIDATIONS.ABBREVIATION.REQUIRED'))
      .max(5, translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.VALIDATIONS.ABBREVIATION.MAX')),
    description: Yup.string()
      .required(translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.VALIDATIONS.DESCRIPTION.REQUIRED')),
  });

  return (
    <Formik<FormData>
      initialValues={{
        abbreviation: '',
        description: '',
        color: getDefaultTagColor(tagCount),
        // Editing
        ...(editMode ? data : {}),
      }}
      validationSchema={VALIDATION_SCHEMA}
      onSubmit={(formData) => handleSubmit(formData)}
    >
      {({ handleSubmit, getFieldProps, setFieldValue, resetForm, errors, touched, isValid, values }) => (
        <form onSubmit={handleSubmit}>
          <div className={'container space-y-6'}>
            <Input
              {...getFieldProps('description')}
              id={'description'}
              label={translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.DESCRIPTION')}
              labelStyle={'bold'}
              minWidthForLabel={MIN_LABEL_WIDTH}
              error={touched.description ? errors.description : null}
              required
            />
            <Input
              {...getFieldProps('abbreviation')}
              id={'abbreviation'}
              label={translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.ABBREVIATION')}
              labelStyle={'bold'}
              hint={translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.HINTS.ABBREVIATION')}
              maxLength={5}
              minWidthForLabel={MIN_LABEL_WIDTH}
              error={touched.abbreviation ? errors.abbreviation : null}
              required
            />

            {!editMode || !data?.isDefault ? (
              <>
                <Field type={'hidden'} value={values.color} name={'color'} />
                <ColorPicker
                  title={translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.LABEL_COLOR')}
                  label={values.abbreviation}
                  value={values.color}
                  onChange={(value ) => setFieldValue('color', value )}
                />
              </>
            ) : null}

            {!isValid && Object.keys(errors).some((key) => touched[key as keyof FormData])
              ? (<FormGenericError />)
              : null}
          </div>

          <ModalFooter justify={editMode ? 'between' : 'end'}>
            {editMode ? (
              <CancelButtonModal
                align={'start'}
                onClick={() => resetForm()}
              >
                {translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.BUTTONS.DISCARD')}
              </CancelButtonModal>
            ) : null}
            <SaveButton
              type={'submit'}
              isLoading={mutations.create.isLoading || mutations.update.isLoading}
            />
          </ModalFooter>

          {showDeleteSection ? (
            <div className={'container space-y-6'}>
              <h5 className={'font-bold'}>
                {translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.DELETE_SECTION_TITLE')}
              </h5>

              {isDefaultTag ? (
                <>
                  <p>{translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.DEFAULT_HINT_1')}</p>
                </>
              ) : hasStudents ? (
                <>
                  <p>{translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.DELETE_HINT_1')}</p>
                  <p>{translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.DELETE_HINT_2')}</p>
                </>
              ) : null}

              <Button
                type={'button'}
                color={'negative'}
                icon={GLOBAL_ICONS.delete}
                isOutline
                onClick={isDefaultTag ? undefined : handleDelete}
                disabled={hasStudents || isDefaultTag}
              >
                {translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.BUTTONS.DELETE')}
              </Button>
            </div>
          ) : null}
        </form>
      )}
    </Formik>
  );
};

export default AddEditTagModal;


export interface ModalProps extends Pick<ToggleModalProps, 'toggle'> {
  data?: TagType;
  tagCount: number;
  onSuccess?: () => void;
  onError?: () => void;
  onDelete?: (tag: TagType) => void;
}

interface FormData {
  abbreviation: string;
  description: string;
  color: number;
}

type TagWithStudentCount = TagType & {
  studentCount?: number | null;
};
